'use client';

import { useState } from 'react';
import { Activity, Flame, ArrowRight, RefreshCw, Calculator } from 'lucide-react';

export default function MetabolismoPage() {
  // Estado para los datos del usuario
  const [formData, setFormData] = useState({
    gender: 'male', // male | female
    age: '',
    weight: '', // kg
    height: '', // cm
    activity: '1.2' // Sedentario por defecto
  });

  const [result, setResult] = useState(null);

  // Lógica de Harris-Benedict (Revisada con documentos TrainAIfit.Health)
  const calculate = (e) => {
    e.preventDefault();
    const { gender, age, weight, height, activity } = formData;
    
    // Validación básica
    if (!age || !weight || !height) return;

    let bmr = 0;
    
    // Fórmulas
    if (gender === 'male') {
      bmr = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
    } else {
      bmr = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
    }

    const tdee = bmr * parseFloat(activity);

    setResult({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee)
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header de la Herramienta */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Activity className="w-4 h-4" /> TrainAIfit.Health Biohacking
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Calculadora Metabólica IA
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Descubre cuánta energía consume tu cuerpo en reposo y cuánta necesitas para lograr tus objetivos (Hipertrofia, Déficit, Mantenimiento).
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          
          {/* COLUMNA IZQUIERDA: FORMULARIO */}
          <div className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100">
            <form onSubmit={calculate} className="space-y-6">
              
              {/* Género */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'male'})}
                  className={`p-4 rounded-xl border-2 font-bold text-center transition-all ${formData.gender === 'male' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-100 text-slate-400 hover:border-blue-200'}`}
                >
                  Hombre
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'female'})}
                  className={`p-4 rounded-xl border-2 font-bold text-center transition-all ${formData.gender === 'female' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-100 text-slate-400 hover:border-pink-200'}`}
                >
                  Mujer
                </button>
              </div>

              {/* Datos Biométricos */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Edad</label>
                  <input 
                    type="number" required
                    className="w-full p-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none font-bold text-slate-900"
                    placeholder="Años"
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Peso (kg)</label>
                  <input 
                    type="number" required
                    className="w-full p-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none font-bold text-slate-900"
                    placeholder="kg"
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Altura (cm)</label>
                  <input 
                    type="number" required
                    className="w-full p-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none font-bold text-slate-900"
                    placeholder="cm"
                    onChange={(e) => setFormData({...formData, height: e.target.value})}
                  />
                </div>
              </div>

              {/* Nivel de Actividad */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Nivel de Actividad</label>
                <select 
                  className="w-full p-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-slate-700 font-medium bg-white"
                  onChange={(e) => setFormData({...formData, activity: e.target.value})}
                >
                  <option value="1.2">Sedentario (Poco o nada ejercicio)</option>
                  <option value="1.375">Ligero (Ejercicio 1-3 días/sem)</option>
                  <option value="1.55">Moderado (Ejercicio 3-5 días/sem)</option>
                  <option value="1.725">Fuerte (Ejercicio 6-7 días/sem)</option>
                  <option value="1.9">Atleta Profesional (Entrenamientos dobles)</option>
                </select>
              </div>

              <button 
                type="submit"
                className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" /> Calcular Metabolismo
              </button>
            </form>
          </div>

          {/* COLUMNA DERECHA: RESULTADOS */}
          <div className="flex flex-col gap-6">
            {!result ? (
              // Estado Vacío
              <div className="h-full bg-white p-8 rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-center text-slate-400">
                <Flame className="w-16 h-16 mb-4 opacity-20" />
                <h3 className="text-lg font-bold mb-2">Esperando datos...</h3>
                <p className="text-sm">Completa tu perfil biométrico para generar tu diagnóstico metabólico.</p>
              </div>
            ) : (
              // Resultados
              <>
                <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10"></div>
                  <h3 className="text-blue-100 text-sm font-bold uppercase tracking-wider mb-2">Tu Metabolismo Basal (BMR)</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black">{result.bmr}</span>
                    <span className="text-lg font-medium opacity-80">kcal/día</span>
                  </div>
                  <p className="mt-4 text-sm text-blue-100 leading-relaxed">
                    Esta es la energía que tu cuerpo quema solo por existir (respirar, pensar, latir). 
                    <strong className="text-white block mt-1">¡Nunca comas menos de esto!</strong>
                  </p>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-lg">
                  <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-4">Gasto Energético Real (TDEE)</h3>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                      <Flame className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="block text-3xl font-black text-slate-900">{result.tdee} kcal</span>
                      <span className="text-xs text-slate-500">Para mantener tu peso actual</span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-6 border-t border-slate-100">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Perder Grasa (Déficit)</span>
                      <span className="font-bold text-emerald-600">{Math.round(result.tdee * 0.85)} kcal</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Ganar Músculo (Superávit)</span>
                      <span className="font-bold text-blue-600">{Math.round(result.tdee * 1.10)} kcal</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}


